import { useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField } from '@ds/forms';
import { TAMANHO_MAXIMO_DO_ARQUIVO, lerModeloDoArquivo, nomeDoArquivoDoModelo } from '@/data/modeloDeFichaEmArquivo';
import type { CampoDeFicha, ModeloDeFicha } from '@/data/tipos';

/** O `FileReader` e não `File.text()`: é o que todo navegador e o jsdom têm. */
function lerTexto(arquivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result ?? ''));
        leitor.onerror = () => reject(leitor.error ?? new Error('Não foi possível ler o arquivo.'));
        leitor.readAsText(arquivo);
    });
}

export interface ModeloLido {
    nome: string;
    campos: CampoDeFicha[];
}

export interface ArquivoDeModeloModalProps {
    modelos: ModeloDeFicha[];
    onFechar: () => void;
    onExportar: (modelo: ModeloDeFicha) => void;
    /** O modelo lido vai para o editor SEM salvar: importar não publica nada sozinho. */
    onImportar: (lido: ModeloLido) => void;
}

/** Importar ou exportar modelo — porte do modal de `FichaBuilderScreen.jsx`. */
export function ArquivoDeModeloModal({ modelos, onFechar, onExportar, onImportar }: ArquivoDeModeloModalProps) {
    const idArquivo = useId();
    const [lido, setLido] = useState<ModeloLido | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [arrastando, setArrastando] = useState(false);

    const escolher = async (arquivo: File | undefined) => {
        setErro(null);
        setLido(null);
        if (!arquivo) return;
        if (!/\.json$/i.test(arquivo.name)) {
            setErro('Envie um arquivo .json exportado da Fórmula MED.');
            return;
        }
        if (arquivo.size > TAMANHO_MAXIMO_DO_ARQUIVO) {
            setErro('O arquivo passa de 2 MB.');
            return;
        }
        try {
            const leitura = lerModeloDoArquivo(await lerTexto(arquivo), arquivo.name);
            if (leitura.ok) setLido({ nome: leitura.nome, campos: leitura.campos });
            else setErro(leitura.erro);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler o arquivo.');
        }
    };

    return (
        <Modal
            open width={560} onClose={onFechar}
            title="Importar ou exportar modelo"
            description="Arquivo .json no formato da Fórmula MED — a rota de migração entre instalações e o backup que a clínica leva embora."
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    <Button disabled={!lido} onClick={() => { if (lido) onImportar(lido); }}>Importar para o editor</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div
                    onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={(e) => { e.preventDefault(); setArrastando(false); void escolher(e.dataTransfer.files[0]); }}
                    style={{
                        border: `1.5px dashed ${arrastando ? 'var(--burgundy-700)' : 'var(--border-strong)'}`,
                        background: arrastando ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                        borderRadius: 'var(--radius-card)', padding: '20px 18px', transition: 'var(--transition-control)',
                    }}
                >
                    <FormField label="Arquivo do modelo" htmlFor={idArquivo} hint="Arraste aqui ou escolha no computador · .json até 2 MB">
                        <input
                            id={idArquivo} type="file" accept=".json,application/json"
                            onChange={(e) => { void escolher(e.target.files?.[0]); }}
                            style={{ fontFamily: 'var(--font-ui)', fontSize: 13 }}
                        />
                    </FormField>
                </div>

                {erro && <Alert tone="warning" title="Arquivo recusado" dismissible={false}>{erro}</Alert>}
                {lido && (
                    <Alert tone="success" title={`${lido.nome} · ${lido.campos.length} ${lido.campos.length === 1 ? 'campo' : 'campos'}`} dismissible={false}>
                        Abre no editor sem salvar — você confere e decide se salva.
                    </Alert>
                )}

                <div style={{ paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                    <p style={{ margin: '0 0 9px', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Exportar</p>
                    {modelos.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum modelo para exportar.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                            {modelos.map((m) => (
                                <li key={m.id}>
                                    <button
                                        type="button" onClick={() => onExportar(m)} aria-label={`Exportar ${m.nome}`}
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xs)', cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        <Icon name="download" size={14} style={{ color: 'var(--neutral-600)' }} />
                                        <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-primary)' }}>{m.nome}</span>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-tertiary)' }}>{nomeDoArquivoDoModelo(m.nome)}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </Modal>
    );
}
